import clsx from 'clsx';
import { isEmpty, isString } from 'lodash';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Outlet } from 'react-router-dom';

import {
  Banner,
  BookingBloc,
  HeroBanner,
  Icon,
  Stepper,
  TravelPurchaseSummary,
  useTravelPurchaseContext
} from 'components';
import { useFetch } from 'hooks';
import { appService } from 'services';
import { navigateToPreviousTravelStep } from 'store';

import travelHero from 'assets/img/travel-hero.png';
import 'assets/styles/components/purchase-tunnel/purchase-tunnel.scss';

const TravelPurchasePage = () => {
  const { prev, currentStep, navigateToStep, travelDeparture, isConfirmationStep } = useTravelPurchaseContext();
  const travelPurchaseSteps = useSelector((state) => state.travelPurchase.steps);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const {
    call: travelPageCall,
    data: travelPageResponse,
    loading: loadingTravelPage
  } = useFetch({ filterUserCompany: true });

  const travelPage = travelPageResponse?.data?.[0];

  const heroData = useMemo(() => {
    if (isEmpty(travelPage)) {
      // Display default hero image and title if there are no content in Strapi
      return {
        heroTitle: t('yourTravel'),
        heroImage: { frontUrl: travelHero }
      };
    } else {
      return travelPage;
    }
  }, [travelPage]);

  // -----------------------------------------------------------------------------------------------------
  // Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------
  useEffect(() => {
    getTravelPageData();
  }, []);

  // -----------------------------------------------------------------------------------------------------
  // Methods
  // -----------------------------------------------------------------------------------------------------
  const getTravelPageData = async () => {
    travelPageCall({
      service: appService.getStrapiServiceName(),
      method: 'get',
      path: '/api/purchase-pages'
    });
  };

  const handleStepClick = (stepIndex) => {
    if (stepIndex < currentStep.order) {
      const previousStep = travelPurchaseSteps.find((step) => step.order === stepIndex);
      // Update step in the store
      dispatch(navigateToPreviousTravelStep(stepIndex));

      // Navigate to previous step
      navigateToStep(previousStep.slug);
    }
  };

  return (
    <>
      <HeroBanner
        title={heroData?.heroTitle}
        image={heroData?.heroImageDesktop}
        mobileImage={heroData?.heroImageMobile}
        loading={loadingTravelPage}
      />

      <div className="main-container bg-gray-100 py-20">
        {!isConfirmationStep && (
          <button type="button" onClick={prev} data-test-id="purchaseTunnelBackButton" className="w-fit">
            <Icon name="angle-left" classes="text-gray-500 mr-2"></Icon>
            <span>{t('buttons.goBack')}</span>
          </button>
        )}

        {/* ERROR MESSAGE IF TRAVEL WAS NOT FOUND */}
        {isString(travelDeparture) && (
          <div className="min-h-[50vh]">
            <Banner type="error" className="my-6 w-fit">
              <p>{travelDeparture}</p>
            </Banner>
          </div>
        )}

        {!isString(travelDeparture) && (
          <>
            {!isConfirmationStep && (
              <Stepper steps={travelPurchaseSteps} onStepClick={handleStepClick} className="my-6 lg:my-12" />
            )}

            <div className="lg:flex lg:gap-x-8">
              <div className="lg:grow">
                <Outlet />
              </div>

              {/* Travel Summary and Contact Blocks */}
              <div className="lg:shrink-0 lg:w-96 lg:max-w-64 lg:flex lg:flex-col lg:gap-8 xl:max-w-sm">
                <TravelPurchaseSummary className={clsx(currentStep.slug === 'validation' && 'hidden lg:block')} />

                {!isEmpty(travelPage?.reservationBlocs) && (
                  <BookingBloc
                    className="hidden lg:block"
                    bookingBlocLoading={loadingTravelPage}
                    blocks={travelPage?.reservationBlocs}
                  />
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default TravelPurchasePage;
